// Standby calculator – pure functions, no DOM.

export const DEFAULT_PRICE = 0.35;
export const CHART_MAX_BARS = 10;

export function kwhPerYear(watt, hoursPerDay) {
  return (watt * hoursPerDay * 365) / 1000;
}

/** Items that can be calculated (pending rows have no value yet). */
export function usableItems(data) {
  return data.items.filter((i) => !i.pending && Number.isFinite(i.typ));
}

/**
 * input: {
 *   counts: { id: n }, custom: { id: watt }, price,
 *   hours: default hours/day (24), groupHours: { groupId: hours }
 * }
 */
export function calculate(data, input) {
  const price = input.price ?? DEFAULT_PRICE;
  const counts = input.counts || {};
  const custom = input.custom || {};
  const hoursFor = (group) => input.groupHours?.[group] ?? input.hours ?? 24;

  const rows = [];
  let watt = 0;
  let kwh = 0;
  let min = 0;
  let max = 0;
  for (const item of usableItems(data)) {
    const count = counts[item.id] || 0;
    if (count <= 0) continue;
    const own = Number.isFinite(custom[item.id]);
    const unitWatt = own ? custom[item.id] : item.typ;
    const hours = hoursFor(item.group);
    const w = count * unitWatt;
    const e = kwhPerYear(w, hours);
    rows.push({ id: item.id, label: item.label, group: item.group, count, unitWatt, own, hours, watt: w, kwh: e, eur: e * price, source: own ? 'eigen' : item.source, item });
    watt += w;
    kwh += e;
    min += count * (own ? unitWatt : item.min ?? unitWatt);
    max += count * (own ? unitWatt : item.max ?? unitWatt);
  }
  for (const r of rows) r.share = watt > 0 ? r.watt / watt : 0;
  rows.sort((a, b) => b.watt - a.watt || a.label.localeCompare(b.label, 'de'));

  const groups = {};
  for (const g of data.groups) groups[g.id] = { count: 0, watt: 0 };
  for (const r of rows) {
    groups[r.group].count += r.count;
    groups[r.group].watt += r.watt;
  }

  return {
    watt,
    kwh,
    eur: kwh * price,
    min,
    max,
    hasSpread: rows.length > 0 && (Math.abs(min - watt) > 1e-9 || Math.abs(max - watt) > 1e-9),
    rows,
    groups,
    price,
  };
}


/** Chart rows: top N−1 categories plus "Sonstige" if more than N. */
export function chartRows(rows, maxBars = CHART_MAX_BARS) {
  if (rows.length <= maxBars) return rows.map((r) => ({ label: r.label, watt: r.watt, share: r.share }));
  const top = rows.slice(0, maxBars - 1).map((r) => ({ label: r.label, watt: r.watt, share: r.share }));
  const rest = rows.slice(maxBars - 1);
  top.push({ label: `Sonstige (${rest.length})`, watt: rest.reduce((s, r) => s + r.watt, 0), share: rest.reduce((s, r) => s + r.share, 0), other: true, labels: rest.map((r) => r.label) });
  // „Sonstige“ is placed by its value, so the longest bar is always on top.
  return top.sort((a, b) => b.watt - a.watt);
}
